import { create } from 'zustand';
import { applyDTODelta } from '@hexcrawl/shared-utils';
import type {
  AppliedEffectDTO,
  EquipmentSlot,
  EquipmentSlotsDTO,
  HexCoordinateDTO,
  IItemInstanceDTO,
  UseItemResponseDTO,
} from '@hexcrawl/shared-utils';
import { useGameStore } from './gameStore';
import { useSpriteStore } from './spriteStore.ts';
import { useDiscoveryStore } from './discoveryStore.ts';
import { inventoryApiService } from '../services/inventoryApiService.ts';

/** The last consumable use, for the inventory panel's info area. */
export interface ItemUseResult {
  itemName: string;
  useText: string;
  appliedEffects: AppliedEffectDTO[];
}

interface InventoryState {
  inventory: IItemInstanceDTO[];
  equipment: EquipmentSlotsDTO;
  abilities: string[];
  lastUse: ItemUseResult | null;
  setInventory: (inventory: IItemInstanceDTO[]) => void;
  setEquipment: (equipment: EquipmentSlotsDTO) => void;
  setAbilities: (abilities: string[]) => void;
  dropItem: (characterId: string, instanceId: string) => Promise<void>;
  equipItem: (characterId: string, instanceId: string, slot: EquipmentSlot) => Promise<void>;
  unequipItem: (characterId: string, slot: EquipmentSlot) => Promise<void>;
  useItem: (gameId: string, instanceId: string, targetHex?: HexCoordinateDTO) => Promise<UseItemResponseDTO>;
  clearLastUse: () => void;
}

function applyEquipResultToGameStore(
  inventory: IItemInstanceDTO[],
  equipment: EquipmentSlotsDTO,
  abilities: string[],
): void {
  const gs = useGameStore.getState().gameState;
  if (!gs?.character) return;
  useGameStore.getState().setGameState({
    ...gs,
    character: { ...gs.character, inventory, equipment, abilities },
  });
}

export const useInventoryStore = create<InventoryState>()((set, get) => ({
  inventory: [],
  equipment: {},
  abilities: [],
  lastUse: null,
  setInventory: (inventory) => set({ inventory }),
  setEquipment: (equipment) => set({ equipment }),
  setAbilities: (abilities) => set({ abilities }),

  dropItem: async (characterId, instanceId) => {
    const result = await inventoryApiService.dropItem(characterId, instanceId);
    set({ inventory: result.inventory });
    const gameId = useGameStore.getState().gameState?.id;
    if (gameId) void useDiscoveryStore.getState().fetchDiscoveries(gameId);
  },

  equipItem: async (characterId, instanceId, slot) => {
    const result = await inventoryApiService.equipItem(characterId, instanceId, slot);
    set({ inventory: result.inventory, equipment: result.equipment, abilities: result.abilities });
    applyEquipResultToGameStore(result.inventory, result.equipment, result.abilities);
    void useSpriteStore.getState().requestRegeneration(characterId);
  },

  unequipItem: async (characterId, slot) => {
    const result = await inventoryApiService.unequipItem(characterId, slot);
    set({ inventory: result.inventory, equipment: result.equipment, abilities: result.abilities });
    applyEquipResultToGameStore(result.inventory, result.equipment, result.abilities);
    void useSpriteStore.getState().requestRegeneration(characterId);
  },

  useItem: async (gameId, instanceId, targetHex) => {
    // Captured before the call: the instance is gone from the store once the delta lands.
    const itemName = get().inventory.find(i => i.id === instanceId)?.def.name ?? 'the item';

    const result = await inventoryApiService.useItem(gameId, instanceId, targetHex);
    // HP/MP and the reduced inventory both arrive inside the delta — the store's
    // gameStore subscription then refreshes `inventory` from it.
    const gameState = useGameStore.getState().gameState;
    if (gameState) {
      useGameStore.getState().setGameState(applyDTODelta(gameState, result.stateDelta));
    }
    set({ lastUse: { itemName, useText: result.useText, appliedEffects: result.appliedEffects ?? [] } });
    // Returned whole: a use that relocates the player carries a hex entry's worth of
    // consequences (encounter, combat, discoveries, loot) that only MapScene can play.
    return result;
  },

  clearLastUse: () => set({ lastUse: null }),
}));

// Sync inventory store whenever game state changes
useGameStore.subscribe((state) => {
  const character = state.gameState?.character;
  useInventoryStore.getState().setInventory(character?.inventory ?? []);
  useInventoryStore.getState().setEquipment(character?.equipment ?? {});
  useInventoryStore.getState().setAbilities(character?.abilities ?? []);
});
